import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { INSP_PRIORITIES, MEASURE_STATUS_OPTIONS } from "@/lib/inspectionConstants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const EMPTY = {
  title: "", description: "", asset_label: "", found_date: "", responsible: "", priority: "normal",
  required_action: "", due_date: "", status: "offen", resolution_note: "",
  inspection_id: null, record_id: null, asset_ids: [],
};

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

export default function MeasureForm({ open, onClose, onSaved, initial, prefill }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!initial;

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial } : { ...EMPTY, found_date: new Date().toISOString().slice(0, 10), ...(prefill || {}) });
  }, [open, initial, prefill]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Titel ist erforderlich");
    setSaving(true);
    try {
      if (editing) await api.put(`/inspections/measures/${initial.id}`, form);
      else await api.post("/inspections/measures", form);
      toast.success(editing ? "Maßnahme aktualisiert" : "Maßnahme angelegt");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-xl">{editing ? "Maßnahme bearbeiten" : "Neue Maßnahme (Mangel)"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1" data-testid="measure-form">
          <Field label="Titel / Mangel *"><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="measure-title" placeholder="z. B. Hydraulik Patientenlifter undicht" /></Field>
          <Field label="Beschreibung"><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Betroffene Anlage"><Input value={form.asset_label} onChange={(e) => set("asset_label", e.target.value)} data-testid="measure-asset" /></Field>
            <Field label="Feststellungsdatum"><Input type="date" value={form.found_date || ""} onChange={(e) => set("found_date", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Verantwortlicher"><Input value={form.responsible} onChange={(e) => set("responsible", e.target.value)} /></Field>
            <Field label="Frist zur Behebung"><Input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} data-testid="measure-due" /></Field>
          </div>
          <Field label="Erforderliche Maßnahme"><Textarea value={form.required_action} onChange={(e) => set("required_action", e.target.value)} rows={2} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Priorität">
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="measure-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{INSP_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="measure-status"><SelectValue /></SelectTrigger>
                <SelectContent>{MEASURE_STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Erledigungsvermerk / Nachweis"><Textarea value={form.resolution_note} onChange={(e) => set("resolution_note", e.target.value)} rows={2} /></Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={saving} data-testid="save-measure-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Speichern" : "Anlegen")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
